import { OverlayContainer } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  Directive,
  DOCUMENT,
  effect,
  ElementRef,
  HostBinding,
  inject,
  Injector,
  NgZone,
  OnInit,
  Renderer2,
  Version,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
  Event as RouterEvent,
} from '@angular/router';
import { CoreStore, MetaStore, UserStore } from '@qtek/libs/store';
import { Environment } from '@qtek/shared/models';
import { firstValueFrom, from, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import {
  ApiCoreService,
  APP_ENVIRONMENT,
  APP_VERSION,
} from '@qtek/core/api-core';
import {
  LocalStorageManagementService,
  LocalStorageStore,
} from '@qtek/core/local-storage-management';
import { WebSocketService } from '@qtek/core/websockets-core';
import { MetaCoreStore } from '@qtek/libs/meta-core';
import { PermissionType } from '@qtek/libs/permissions';
import { SnackBarService } from '@qtek/libs/snack-bar-core';
import { TranslationCoreService } from '@qtek/libs/translation-core';
import {
  loadQuillStyle$,
  patchQuillLinkSanitizer,
  StyledMentionBlot,
} from '@qtek/shared/utils';
import { QuillService } from 'ngx-quill';
import { Mention } from 'quill-mention';
import { QT_INTLS, QtIntl } from './intl';
import { LoaderComponent } from './loader/loader.component';

// Extended components do not inherit metadata so we export it here.
export const APP_COMPONENT_DEFAULT_TEMPLATE = `
<div>
  <mat-progress-bar #progressBar mode="indeterminate" color="accent"></mat-progress-bar>
  <router-outlet></router-outlet>
</div>
`;

export const APP_COMPONENT_DEFAULT_STYLES = `
.mat-mdc-progress-bar {
    z-index: 2;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    opacity: 0;
  }
`;

@Directive()
export abstract class QtAppComponent implements OnInit, AfterViewInit {
  private _ngZone: NgZone = inject(NgZone);
  private _renderer: Renderer2 = inject(Renderer2);
  private _matIconRegistry: MatIconRegistry = inject(MatIconRegistry);
  private _sanitizer: DomSanitizer = inject(DomSanitizer);
  private _router: Router = inject(Router);
  private _metaCoreStore = inject(MetaCoreStore);
  private _localStorageStore = inject(LocalStorageStore);
  private _userStore = inject(UserStore);
  private _coreStore = inject(CoreStore);
  private metaStore = inject(MetaStore);
  private _injector: Injector = inject(Injector);
  private _document: any = inject(DOCUMENT);
  private _intls: QtIntl[] = inject<QtIntl[]>(QT_INTLS);
  private _route: ActivatedRoute = inject(ActivatedRoute);
  private _viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
  private _translateService: TranslationCoreService = inject(
    TranslationCoreService
  );
  private _overlay: OverlayContainer = inject(OverlayContainer);
  private _apiCoreService: ApiCoreService = inject(ApiCoreService);
  private _webSocketService: WebSocketService = inject(WebSocketService);
  private _localStorageManagementService: LocalStorageManagementService =
    inject(LocalStorageManagementService);
  private _snackbarService: SnackBarService = inject(SnackBarService);
  private _environment: Environment = inject(APP_ENVIRONMENT);
  private _version: Version = inject(APP_VERSION);
  private _quill: QuillService = inject(QuillService);

  @ViewChild('progressBar', { read: ElementRef })
  progressBar: ElementRef;
  @HostBinding('class') className = '';

  protected readonly permissionView = PermissionType.View as string;

  themeSub: Subscription;
  constructor() {
    this.metaStore.loadGuiDomain();
    // Change language
    effect(() => {
      const person = this._userStore.currentPerson();
      if (person?.language) {
        this._translateService.setLanguage(person.language);
      }
    });
  }

  ngOnInit() {
    this._metaCoreStore.loadPortalMeta();
    this._metaCoreStore.loadLanguages();
    this._metaCoreStore.loadWebsocket();
    this._localStorageStore.refresh();
    // create component on first occurrence
    toObservable(this._coreStore.displayLoading, { injector: this._injector })
      .pipe(filter(Boolean), take(1))
      .subscribe(() => this.createLoaderComponent());

    // replace material icons with symbols font
    const defaultFontSetClasses =
      this._matIconRegistry.getDefaultFontSetClass();
    const outlinedFontSetClasses = defaultFontSetClasses
      .filter(fontSetClass => fontSetClass !== 'material-icons')
      .concat(['material-symbols-outlined']);
    this._matIconRegistry.setDefaultFontSetClass(...outlinedFontSetClasses);

    from(this._intls).subscribe(intl => {
      this._translateService
        .getTranslations(intl.getTranslations())
        .subscribe(res => {
          intl.updateTranslations(res);
        });
    });

    this._quill.config.beforeRender = () => firstValueFrom(loadQuillStyle$);
    this._quill.config.customModules = [
      { implementation: StyledMentionBlot, path: 'blots/mention' },
      { implementation: Mention, path: 'modules/mention' },
    ];
    patchQuillLinkSanitizer();
  }

  ngAfterViewInit(): void {
    this._router.events.subscribe((event: RouterEvent) => {
      this.navigationInterceptor(event);
    });
  }

  private navigationInterceptor(event: RouterEvent): void {
    if (
      event instanceof NavigationStart ||
      event instanceof RouteConfigLoadStart
    ) {
      this.updateOpacity(1);
    } else if (
      event instanceof NavigationEnd ||
      event instanceof NavigationCancel ||
      event instanceof NavigationError ||
      event instanceof RouteConfigLoadEnd
    ) {
      this.updateOpacity(0);
    }
  }

  private updateOpacity(value: 0 | 1): void {
    this._ngZone.runOutsideAngular(() => {
      this._renderer.setStyle(this.progressBar.nativeElement, 'opacity', value);
    });
  }

  private createLoaderComponent(): void {
    const componentRef =
      this._viewContainerRef.createComponent(LoaderComponent);
    componentRef.changeDetectorRef.detectChanges();
  }
}
